import React, { useCallback, useEffect, useRef, useState } from "react"

import { ChevronLeft, ChevronRight, FileDown, Loader2, Mail, MoreVertical } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useApplicationsByStage } from "@/hooks/useApplicationsByStage"
import { useEmailApplicantsByStage } from "@/hooks/useEmailApplicantsByStage"
import { fetchAllApplicationsByStage } from "@/services/fetchAllApplicationsByStage"

import { Application, FilterState } from "../types/application"
import ApplicationCard from "./ApplicationCard"
import RoleTeamGuard from "./guards/RoleTeamGuard"

interface KanbanColumnWithInfiniteScrollProps {
  title: string
  stageId: string
  icon?: React.ReactNode
  color?: string
  searchQuery?: string
  filters?: Partial<FilterState>
  onDragStart: (application: Application) => void
  onDragEnd: () => void
  onDrop: () => void
  onStatusChange?: (id: string, status: string) => void
  initialCollapsed?: boolean
}

const KanbanColumnWithInfiniteScroll: React.FC<KanbanColumnWithInfiniteScrollProps> = ({
  title,
  stageId,
  icon,
  color = "#64748b", // Default color if none provided
  onDragStart,
  onDragEnd,
  onDrop,
  onStatusChange,
  searchQuery = "",
  filters = {},
  initialCollapsed = false,
}) => {
  // Use local storage to persist collapsed state
  const storageKey = `kanban-column-${stageId}-collapsed`

  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Check local storage for saved state
    const savedState = localStorage.getItem(storageKey)
    return savedState !== null ? savedState === "true" : initialCollapsed
  })

  // Update local storage when collapsed state changes
  useEffect(() => {
    localStorage.setItem(storageKey, isCollapsed.toString())
  }, [isCollapsed, storageKey])
  const { mutate: emailApplicantsByStage, isPending: isEmailingApplicants } =
    useEmailApplicantsByStage()
  const [isExporting, setIsExporting] = useState(false)
  const {
    applications,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    totalCount,
  } = useApplicationsByStage({
    stage: stageId,
    limit: 10,
    searchQuery,
    filters,
  })

  const observer = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading || isFetchingNextPage) return

      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage()
        }
      })

      if (node) observer.current.observe(node)
    },
    [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage],
  )

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    onDrop()
  }

  // CSV Export for the current stage
  const downloadCSV = (filename: string, rows: string[][]) => {
    const escapeCell = (cell: string) => {
      if (cell == null) return ""
      const needsQuotes = /[",\n]/.test(cell)
      const value = String(cell).replace(/"/g, '""')
      return needsQuotes ? `"${value}"` : value
    }
    const csvContent = rows.map((r) => r.map(escapeCell).join(",")).join("\n")
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleCsvExport = async () => {
    try {
      setIsExporting(true)
      const all = await fetchAllApplicationsByStage({
        stage: stageId,
        searchQuery,
        filters,
      })
      const ts = new Date()
      const pad = (n: number) => String(n).padStart(2, "0")
      const timestamp = `${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(
        ts.getDate(),
      )}_${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}`

      if (stageId === "orientation") {
        const filename = `orientation-stage-applicants-${timestamp}.csv`
        const header = ["first_name", "last_name", "application_id", "orientation_booked_date"]
        const rows: string[][] = [
          header,
          ...all.map((a) => [
            a.firstName ?? "",
            a.lastName ?? "",
            a.application_id ?? "",
            a.orientationBookingDate ?? "",
          ]),
        ]
        downloadCSV(filename, rows)
        return
      }

      if (stageId === "vehicle-collection") {
        const filename = `vehicle-collection-stage-applicants-${timestamp}.csv`
        const header = [
          "first_name",
          "last_name",
          "application_id",
          "vehicle_collection_booked_date",
        ]
        const rows: string[][] = [
          header,
          ...all.map((a) => [
            a.firstName ?? "",
            a.lastName ?? "",
            a.application_id ?? "",
            a.vehicleCollectionBookingDate ?? "",
          ]),
        ]
        downloadCSV(filename, rows)
        return
      }

      // Default fallback per-stage (extend with other stage formats as needed)
      const filename = `${stageId}-stage-applicants-${timestamp}.csv`
      const header = ["application_id"]
      const rows: string[][] = [header, ...all.map((a) => [a.application_id])]
      downloadCSV(filename, rows)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div
      className={`kanban-column ${isCollapsed ? "collapsed" : ""}`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={isCollapsed ? { minWidth: "40px", maxWidth: "40px" } : {}}
    >
      <div
        className={`kanban-column-header ${isCollapsed ? "flex-col h-auto py-3" : ""}`}
        style={{ borderBottomColor: color }}
      >
        {isCollapsed ? (
          // Collapsed header layout
          <>
            <div className="flex justify-center w-full items-center mb-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 p-0"
                onClick={() => setIsCollapsed(false)}
                title="Expand column"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-col items-center gap-3 justify-between h-[250px]">
              <span className="bg-palette-jungleGreen200 text-xs px-2 py-1 rounded-full">
                {applications.length}
                {hasNextPage && "+"}
              </span>
              <div className="flex flex-col items-center gap-2">
                <span className="font-medium vertical-text">{title}</span>
                {icon && <span>{icon}</span>}
              </div>
            </div>
          </>
        ) : (
          // Expanded header layout
          <>
            <div className="flex items-center gap-2">
              {icon && <span>{icon}</span>}
              <span className="font-medium">{title}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="bg-palette-jungleGreen200 text-xs px-2 py-1 rounded-full">
                {totalCount}
              </span>
              <RoleTeamGuard roles={["Admin", "Manager"]}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => setIsCollapsed(true)}
                      className="flex items-center gap-2 cursor-pointer"
                      title="Collapse column"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Collapse
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() =>
                        emailApplicantsByStage({
                          stage: stageId,
                          redirectUrl: import.meta.env.VITE_FRONTEND_URL
                            ? `${import.meta.env.VITE_FRONTEND_URL}/auth-callback`
                            : "https://deliver.hived.space/auth-callback",
                        })
                      }
                      disabled={isEmailingApplicants}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Mail className="h-4 w-4" />
                      {isEmailingApplicants ? "Sending..." : `Email ${title} Applicants`}
                    </DropdownMenuItem>
                    {(stageId === "orientation" || stageId === "vehicle-collection") && (
                      <DropdownMenuItem
                        onClick={handleCsvExport}
                        className="flex items-center gap-2 cursor-pointer"
                        title="Export CSV"
                        disabled={isExporting}
                      >
                        <FileDown className="h-4 w-4" />
                        {isExporting ? "Exporting..." : "CSV Export"}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </RoleTeamGuard>
            </div>
          </>
        )}
      </div>
      {!isCollapsed && (
        <div className="kanban-column-content">
          {isLoading && !applications.length ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-palette-jungleGreen500" />
            </div>
          ) : isError ? (
            <div className="text-center py-4 text-red-500">Error loading applications</div>
          ) : applications.length === 0 ? (
            <div className="text-center py-4 text-palette-jungleGreen500">
              No applications in this stage
            </div>
          ) : (
            <>
              {applications.map((application) => (
                <ApplicationCard
                  key={application.id}
                  application={application}
                  onDragStart={() => onDragStart(application)}
                  onDragEnd={onDragEnd}
                  onStatusChange={onStatusChange}
                />
              ))}
              {hasNextPage && (
                <div ref={loadMoreRef} className="py-2 text-center">
                  {isFetchingNextPage && (
                    <Loader2 className="h-4 w-4 animate-spin mx-auto text-palette-jungleGreen500" />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default KanbanColumnWithInfiniteScroll
